import React from "react";

import { InfoSmIcon, Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import PageMeta from "Components/PageMeta";
import * as Accordion from "ds/Accordion";
import { AddButton, IconControl, Link } from "ds/Button";
import * as Dialog from "ds/Dialog";
import Loading from "ds/PageLoading";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import {
  environmentSelector,
  canManageEnvironmentVariablesSelector
} from "Reducers/environment";
import { useLoadVariables } from "Reducers/environment/settings/variable/hooks/useLoadVariables";
import { gitProjectSelector } from "Reducers/project/project";
import { useLoadVariables as useLoadProjectVariables } from "Reducers/project/settings/variable/hooks/useLoadVariables";
import VariableSection from "src/organization/pages/project/environment/settings/variables/components/VariableSection/VariableSection";
import Empty from "src/organization/pages/project/settings/variables/components/Empty/Empty";

import * as S from "./EnvironmentVariables.styles";

const EnvironmentVariables = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { environmentId, organizationId, projectId } = useDecodedParams<{
    environmentId: string;
    organizationId: string;
    projectId: string;
  }>();
  const project = useSelectorWithUrlParams(gitProjectSelector);
  const environment = useSelectorWithUrlParams(environmentSelector);
  const { variables: projectListVariables, loading: projectLoading } =
    useLoadProjectVariables({
      organizationId,
      projectId
    });
  const { variables, loading, isOverridingFrom, isInactive } = useLoadVariables(
    {
      organizationId,
      projectId,
      environmentId,
      projectListVariables
    }
  );
  const encodedEnvironmentId = encodeURIComponent(environmentId);

  function addVariable() {
    navigate(
      `/${organizationId}/${projectId}/${encodedEnvironmentId}/settings/variables/-/new`
    );
  }
  const canManageEnvironmentVariables = useSelectorWithUrlParams(
    canManageEnvironmentVariablesSelector
  );

  return (
    <div>
      <PageMeta
        title={`Variables | ${environment?.title} | ${project?.title}`}
      />
      <S.HeaderWrapper>
        <S.Heading id="settings-heading" tag="h2">
          {environment?.title || intl.formatMessage({ id: "variables" })}
        </S.Heading>

        {canManageEnvironmentVariables &&
          (variables?.length > 0 || projectListVariables?.length > 0) && (
            <AddButton
              id="add-new-variable"
              analyticId="settings.variables.add.button"
              onClick={addVariable}
            >
              {intl.formatMessage({
                id: "settings.variables.add.button"
              })}
            </AddButton>
          )}
      </S.HeaderWrapper>

      <S.PageDescription>
        <Typography.BodyText size="small">
          {intl.formatMessage({
            id: "settings.environment.variables.description"
          })}
        </Typography.BodyText>
        <S.NoteWrapper>
          <Dialog.Root>
            <Dialog.Trigger>
              <IconControl aria-label="info-trigger-env-variables">
                <InfoSmIcon />
              </IconControl>
            </Dialog.Trigger>
            <S.Content>
              <Typography.BodyText size="small" weight="semiBold">
                {intl.formatMessage({
                  id: "settings.variables.variables"
                })}
              </Typography.BodyText>
              <Typography.BodyText size="small">
                {intl.formatMessage(
                  {
                    id: "settings.variables.details"
                  },
                  {
                    code: txt => <S.Code variant="inline">{txt}</S.Code>,
                    br: <br />,
                    appEnvironmentVariableName: intl.formatMessage({
                      id: "app_environment_variable_name"
                    })
                  }
                )}
              </Typography.BodyText>
            </S.Content>
          </Dialog.Root>
          <div>
            {intl.formatMessage<React.ReactNode>(
              { id: "settings.project.variables.note" },
              {
                link: txt => (
                  <Link
                    href={intl.formatMessage({
                      id: "links.documentation.app.variables"
                    })}
                  >
                    {txt}
                  </Link>
                )
              }
            )}
          </div>
        </S.NoteWrapper>
      </S.PageDescription>

      <section aria-labelledby="settings-heading">
        {loading || projectLoading ? (
          <Loading />
        ) : variables?.length === 0 && projectListVariables?.length === 0 ? (
          <Empty
            onClick={addVariable}
            disabled={!canManageEnvironmentVariables}
            tooltipId="environment.variable.access"
          />
        ) : (
          <Accordion.Root type="multiple" defaultValue={["item1", "item2"]}>
            <Accordion.Item value="item1">
              <S.SectionHeader>
                {intl.formatMessage(
                  {
                    id: "settings.environment.variables.list.environment"
                  },
                  {
                    count: <span>({variables?.length})</span>,
                    title: <>{capitalize(environment?.title)}</>
                  }
                )}
              </S.SectionHeader>
              <Accordion.Content>
                <VariableSection
                  variables={variables}
                  isOverridingFrom={isOverridingFrom}
                />
              </Accordion.Content>
            </Accordion.Item>
            <Accordion.Item value="item2">
              <Accordion.SectionHeader>
                {intl.formatMessage(
                  { id: "settings.environment.variables.list.project" },
                  {
                    count: <span>({projectListVariables?.length || 0})</span>
                  }
                )}
              </Accordion.SectionHeader>
              <Accordion.Content>
                <VariableSection
                  variables={projectListVariables}
                  type="project"
                  isInactive={isInactive}
                />
              </Accordion.Content>
            </Accordion.Item>
          </Accordion.Root>
        )}
      </section>
    </div>
  );
};

export default EnvironmentVariables;
